import { useState } from 'react';
import { Alert, Center, Loader, Stack } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { Automation, AutomationStep, type AutomationWire } from '~api';
import { api } from '~portal/api';
import {
  OutputOptionsPanel,
  toOutputAsset,
} from '~portal/components/admin/automations/OutputOptionsPanel';
import type {
  CollectorOption,
  ProcessorOption,
} from '~portal/components/admin/automations/StepEditor';
import {
  WorkflowDetailView,
  type WorkflowPending,
} from '~portal/components/admin/automations/WorkflowDetailView';
import { AnchorLink } from '~portal/components/common';
import { useCanWriteSourcePolicy } from '~portal/hooks';
import { logger } from '~portal/lib';
import { contractSummary, errorMessage, isConflict } from '~portal/lib/automations';
import { useAutomationCapabilities } from '~portal/lib/automations-capabilities';
import { hostsInConfig, hostsInText, mergeHosts } from '~portal/lib/source-hosts';

/** @desc Collectors whose versions the `collect` step's pin picker loads: admin scale is small. */
const MAX_PINNABLE_COLLECTORS = 25;
const RECENT_RUNS = 10;

type WorkflowSearch = { step?: string };

const parseWorkflowSearch = (search: Record<string, unknown>): WorkflowSearch => {
  const { step } = search;
  return typeof step === 'string' && AutomationStep.ID_PATTERN.test(step) ? { step } : {};
};

/**
 * @desc The latest test run of a collector version, from the collector's recent runs (the version
 * lists its `testRunIds`): the latest loaded one, `{}` when it has tests but none is loaded, null
 * when it has none.
 */
const lastTestOf = (
  version: AutomationWire.CollectorVersion,
  runs: AutomationWire.RunListing[],
): { status?: string; at?: Date } | null => {
  const tests = runs
    .filter((run) => version.testRunIds.includes(run._id))
    .map((run) => ({ status: run.status, at: run.finishedAt ?? run.startedAt ?? run.createdAt }))
    .sort((a, b) => b.at.getTime() - a.at.getTime());
  // `runs` is capped (25 of every kind), so a test run can fall outside it: then it is known to
  // have happened, without a status or time. Only an empty `testRunIds` is "Not tested".
  return tests[0] ?? (version.testRunIds.length > 0 ? {} : null);
};

/**
 * @desc A workflow's detail page (H2-7, task 8.5). Data and writes live here; the page itself is
 * `WorkflowDetailView`. Every query is under `automations.workflow|run|collector|processor|asset`, which the
 * admin room's hints invalidate (D13), so a change made by another admin — or the authoring agent
 * — shows without a reload.
 */
export const Route = createFileRoute('/_protected/admin/ario/workflows_/$workflowId')({
  // `?step=<stepId>` scrolls to and highlights that step (D10): the link from a component's old
  // places (dashboard card, run detail). An id that is not a step slug is dropped.
  validateSearch: parseWorkflowSearch,
  component: WorkflowDetailPage,
});

function WorkflowDetailPage() {
  const { workflowId } = Route.useParams();
  const { step: highlightStepId } = Route.useSearch();
  const utils = api.useUtils();
  const capabilities = useAutomationCapabilities();
  const canWriteSources = useCanWriteSourcePolicy();
  const detail = api.automations.workflow.get.useQuery({ id: workflowId });
  const runs = api.automations.run.list.useQuery({ workflowId, limit: RECENT_RUNS });
  const collectorList = api.automations.collector.list.useQuery({ limit: 100 });
  const pinnable = (collectorList.data ?? [])
    .filter((collector) => !Automation.isArchived(collector))
    .slice(0, MAX_PINNABLE_COLLECTORS);
  // Archived ones too, so a step that still names one shows its name (the picker leaves them out).
  const processorList = api.automations.processor.list.useQuery({
    includeArchived: true,
    limit: 200,
  });
  const collectorDetails = api.useQueries((t) =>
    pinnable.map((collector) => t.automations.collector.get({ id: collector._id })),
  );
  const latestRun = runs.data?.items[0];
  const latestAssets = api.automations.asset.listForRun.useQuery(
    { runId: latestRun?._id ?? '' },
    { enabled: latestRun !== undefined },
  );

  const [pending, setPending] = useState<WorkflowPending>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const saveDraft = api.automations.workflow.saveDraft.useMutation();
  const accept = api.automations.workflow.accept.useMutation();
  const discard = api.automations.workflow.discard.useMutation();
  const restore = api.automations.workflow.restoreVersion.useMutation();
  const setEnabled = api.automations.workflow.setEnabled.useMutation();
  const archive = api.automations.workflow.archive.useMutation();
  const runNow = api.automations.workflow.runNow.useMutation();
  const retry = api.automations.workflow.retry.useMutation();
  const cancelRun = api.automations.workflow.cancelRun.useMutation();

  const refresh = () => {
    setConflict(false);
    setError(null);
    void utils.automations.workflow.get.invalidate({ id: workflowId });
  };

  /**
   * @desc One write: marks it pending, refetches on success (the hint does too, for every other
   * admin), and turns a refusal into the page's error or CONFLICT notice. Resolves `true` when the
   * server accepted it.
   */
  const write =
    <A extends unknown[]>(
      kind: Exclude<WorkflowPending, null>,
      call: (...args: A) => Promise<unknown>,
    ) =>
    async (...args: A): Promise<boolean> => {
      setPending(kind);
      setError(null);
      setConflict(false);
      try {
        await call(...args);
        await Promise.all([
          utils.automations.workflow.invalidate(),
          utils.automations.run.list.invalidate({ workflowId }),
        ]);
        return true;
      } catch (err) {
        if (isConflict(err)) {
          setConflict(true);
        } else {
          setError(errorMessage(err));
          logger.warn('[WorkflowDetail] Write refused', {
            workflowId,
            action: kind,
            error: errorMessage(err),
          });
        }
        return false;
      } finally {
        setPending(null);
      }
    };

  if (detail.isLoading) {
    return (
      <Center h={240}>
        <Loader />
      </Center>
    );
  }
  if (!detail.data) {
    return (
      <Stack p="md" gap="sm">
        <Alert color="red" variant="light" title="This workflow could not be loaded">
          {detail.error?.message ?? 'Not found.'}
        </Alert>
        <AnchorLink to="/admin/ario/workflows">Back to workflows</AnchorLink>
      </Stack>
    );
  }

  const { workflow, draft } = detail.data;
  const token = () => ({ id: workflow._id, updatedAt: workflow.updatedAt });

  const collectors: CollectorOption[] = pinnable.map((collector, index) => ({
    id: collector._id,
    name: collector.name,
    status: collector.status,
    kind: collector.kind,
    currentVersionId: collector.currentVersionId,
    ...(collector.search ? { search: collector.search } : {}),
    ...(collector.excel ? { excel: collector.excel } : {}),
    versions: (collectorDetails[index]?.data?.versions ?? [])
      .filter((version) => version.status === 'accepted')
      .map((version) => ({
        id: version._id,
        ...(version.number ? { number: version.number } : {}),
        ...(version.sourceProfile ? { sourceProfile: version.sourceProfile } : {}),
        contract: contractSummary(version.contracts, version.declaredOutputs),
        lastTest: lastTestOf(version, collectorDetails[index]?.data?.runs ?? []),
      })),
  }));

  // The hosts a collector may read: its feed, and the URLs in its current and draft scripts.
  const collectorHosts = new Map(
    pinnable.map((collector, index) => {
      const detail = collectorDetails[index]?.data;
      return [
        collector._id,
        mergeHosts(
          collector.feed ? hostsInText(collector.feed.url) : [],
          hostsInText(detail?.current?.script ?? ''),
          hostsInText(detail?.draft?.script ?? ''),
        ),
      ] as const;
    }),
  );
  const hostsOf = (step: AutomationStep.Step): string[] => {
    const collectorId = typeof step.config.collectorId === 'string' ? step.config.collectorId : '';
    return mergeHosts(
      hostsInConfig(step.config),
      AutomationStep.isCollectType(step.type) ? (collectorHosts.get(collectorId) ?? []) : [],
    );
  };

  const processors: ProcessorOption[] = (processorList.data ?? []).map((processor) => ({
    id: processor._id,
    name: processor.name,
    type: processor.type,
    status: processor.status,
    current: processor.current
      ? { id: processor.current._id, number: processor.current.number }
      : null,
    ...(processor.current ? { contract: contractSummary(processor.current.contracts) } : {}),
  }));

  return (
    <WorkflowDetailView
      detail={detail.data}
      runs={runs.data?.items ?? []}
      latestOutputs={
        latestRun ? (
          <OutputOptionsPanel
            assets={(latestAssets.data ?? []).map(toOutputAsset)}
            emptyText={latestAssets.isLoading ? 'Loading outputs…' : 'This run has no outputs yet.'}
          />
        ) : undefined
      }
      collectors={collectors}
      processors={processors}
      {...(highlightStepId ? { highlightStepId } : {})}
      capabilities={capabilities}
      hostsOf={hostsOf}
      canWriteSources={canWriteSources}
      pending={pending}
      error={error}
      conflict={conflict}
      onRefresh={refresh}
      actions={{
        saveDraft: write('save', (input) => saveDraft.mutateAsync(input)),
        accept: write('accept', () =>
          accept.mutateAsync({
            workflowId: workflow._id,
            versionId: draft?._id ?? '',
            updatedAt: workflow.updatedAt,
          }),
        ),
        discard: write('discard', (reason: string | undefined) =>
          discard.mutateAsync({
            workflowId: workflow._id,
            versionId: draft?._id ?? '',
            updatedAt: workflow.updatedAt,
            ...(reason ? { reason } : {}),
          }),
        ),
        restore: write('restore', (versionId: string, reason: string) =>
          restore.mutateAsync({ ...token(), versionId, reason }),
        ),
        setEnabled: (enabled: boolean, reason: string | undefined) =>
          write(enabled ? 'enable' : 'disable', () =>
            setEnabled.mutateAsync({ ...token(), enabled, ...(reason ? { reason } : {}) }),
          )(),
        archive: write('archive', (reason: string) => archive.mutateAsync({ ...token(), reason })),
        runNow: (sample: boolean) =>
          write(sample ? 'run' : 'full-run', () =>
            runNow.mutateAsync({ workflowId: workflow._id, sample, params: {} }),
          )(),
        retry: write('retry', (runId: string, reason: string | undefined) =>
          retry.mutateAsync({ id: runId, ...(reason ? { reason } : {}) }),
        ),
        cancel: write('cancel', (runId: string, reason: string) =>
          cancelRun.mutateAsync({ id: runId, reason }),
        ),
      }}
    />
  );
}
